import { FC, useState } from 'react';
import { Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import AuthWrapper from '../../common/AuthWrapper';
import CustomInput from '../../common/CustomInput';
import CustomPassInput from '../../common/CustomPassInput';
import CustomButton from '../../common/CustomButton';

import { useLoginUserMutation } from '../../store/services/authApi';
import { useAppDispatch } from '../../hooks/redux';
import { setFirstLoginStep } from '../../store/slices/authSlice';
import { useIsMobile } from '../../hooks/useWindowWidth';

import styles from './Login.module.scss';

const Login: FC = () => {
  const [loginUser] = useLoginUserMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();

  const [resErrors, setResErrors] = useState<{attr: string | null, code: string, detail: string}[] | null>(null);

  const initialValues = {
    email: '',
    password: '',
  };

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      const result = await loginUser({
        email: values.email,
        password: values.password,
      }).unwrap();

      dispatch(setFirstLoginStep(true));
      localStorage.setItem('userEmail', values.email);
      navigate("/reset-password");

      if (result.access) {
        localStorage.setItem("authToken", result.access);
      }
      setResErrors(null);
    } catch (error) {
      console.error("Error logging in:", error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const formikValidate = (values: { email: string; password: string }) => {
    const errors: { email?: string; password?: string } = {};
    if (!values.email) {
      errors.email = 'Email is required';
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
    ) {
      errors.email = 'Invalid email address';
    }
    if (!values.password && !errors.email) {
      errors.password = 'Password can\'t be empty';
    }
    if (values.password.length < 6) {
      errors.password = 'Password address is too short';
    }
    return errors;
  }

  return (
    <AuthWrapper btnName='Sign Up'>
      <Box className={styles.loginContent}>
        <Typography variant={`${isMobile ? 'h6Bold' : 'h5Bold'}`} className={styles.title}>Login</Typography>
        <Formik
          initialValues={initialValues}
          validate={formikValidate}
          onSubmit={handleSubmit}
        >
          {({ values, errors, handleChange, isSubmitting }) => (
            <Form className={styles.loginForm}>

                <CustomInput
                  inputType='email'
                  inputName="email"
                  value={values.email}
                  error={errors.email}
                  customClass={styles.customInput}
                  onChange={handleChange}
                />
                <CustomPassInput
                  label='Password'
                  error={errors.password}
                  customClass={styles.customInput}
                  onChange={handleChange}
                />

                <Link to='/forgot-password' className={styles.forgotPassLink}>
                  <Typography variant='h11Semibold'>Forgot password?</Typography>
                </Link>
                <CustomButton
                  btnType='submit'
                  customClass={styles.customButton}
                  disabled={isSubmitting}
                >
                  Log In
                </CustomButton>

                {resErrors && resErrors.map((error, index) => (
                  <Typography key={index} variant='h12' sx={{ color: '#C8102E' }}>
                    {error.detail}
                  </Typography>
                ))}
            </Form>)}
        </Formik>

        <Box className={styles.signUpQuestion}>
          <Typography variant='h11' className={styles.signUpQuestionText}>Don't have an account?</Typography>
          <Link to='/sign-up' className={styles.signUpLink}>
            <Typography variant='h11Semibold'>Sign Up</Typography>
          </Link>
        </Box>
      </Box>
    </AuthWrapper>
  )
};

export default Login;
